import Links from '@/shared/constants/navegacao';
import { Column, DataTable } from '@/shared/components/tables/DataTable';
import { DataAmigavel, MESES, diasDaSemana } from '@/shared/utils/datas';
import { GetGastoProjecaoProximaCompra, GetGastos } from './gasto.service';
import { ModalProjecaoProximaCompra } from './ModalProjecaoProximaCompra';
import { ReadGastoProjecaoDto } from './gasto-projecao.types';
import { GetTipoGastos } from '../tipo-gasto/tipo-gasto.service';
import { Link } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { ReadGastoDto, ReadTipoGastoDto } from '@/api.types';
import { toast } from 'react-toastify';
import { useDataTableState } from '@/shared/components/tables/useDataTableState';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { useEffect, useState, useMemo } from 'react';

type Visualizacao = 'calendario' | 'lista';

export default function GastoIndex() {
  const { setActions } = useBreadcrumbActions();
  const [gastos, setGastos] = useState<ReadGastoDto[]>([]);
  const [tipoGastos, setTipoGastos] = useState<ReadTipoGastoDto[]>([]);
  const [tipoGastoFiltro, setTipoGastoFiltro] = useState<number | undefined>(undefined);
  const [ano, setAno] = useState(new Date().getFullYear());
  const [visualizacao, setVisualizacao] = useState<Visualizacao>('calendario');
  const [modalGastoAberto, setModalGastoAberto] = useState(false);
  const [gastosSelecionados, setGastosSelecionados] = useState<ReadGastoDto[]>([]);
  const [modalProjecaoAberto, setModalProjecaoAberto] = useState(false);
  const [projecaoLoading, setProjecaoLoading] = useState(false);
  const [projecao, setProjecao] = useState<ReadGastoProjecaoDto | null>(null);
  const tableState = useDataTableState('data');

  const abrirModalGasto = (gastos: ReadGastoDto[]) => {
    setGastosSelecionados(gastos);
    setModalGastoAberto(true);
  };

  const handleBuscar = () => {
    GetGastos(0, 1000, tipoGastoFiltro)
      .then((result) => {
        setGastos(result.objects);
      })
      .catch((res) => {
        toast.error(String(res) || '');
      });
  }

  useEffect(() => {
    GetTipoGastos(0, 1000)
      .then((result) => {
        setTipoGastos(result.objects);
      })
      .catch((res) => {
        toast.error(String(res));
      });
  }, []);

  useEffect(() => {
    setActions({
      primary: { label: 'Cadastrar', href: Links.Gasto_Criar, onClick: () => { } },
    });
    return () => setActions({});
  }, [setActions]);

  useEffect(() => {
    handleBuscar();
  }, [tipoGastoFiltro]);

  // Filtrar gastos por ano
  const gastosFiltradosPorAno = useMemo(() => {
    if (visualizacao === 'calendario') {
      return gastos;
    }
    return gastos.filter((gasto) => {
      if (!gasto.data) return false;
      const dataGasto = new Date(gasto.data);
      return dataGasto.getFullYear() === ano;
    });
  }, [gastos, ano, visualizacao]);

  // Colunas para o DataTable
  const colunas: Column<ReadGastoDto>[] = [
    {
      key: "data",
      header: "Data",
      render: (row) => {
        if (row.data) {
          return DataAmigavel(row.data);
        }
        return '';
      }
    },
    {
      key: "quemGastou",
      header: "Quem Gastou",
      render: (row) => {
        if (row.quemGastou) {
          return `${row.quemGastou.nome} ${row.quemGastou.sobrenome || ''}`.trim();
        }
        return '';
      }
    },
    {
      key: "tipoGasto.descricao",
      header: "Tipo de Gasto"
    },
    {
      key: "valor",
      header: "Valor",
      render: (row) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(row.valor) || 0);
      }
    },
    {
      key: "acoes", header: "Ações", render: (row) =>
        <Link className="link-secondary" to={Links.Gasto_Editar + '/' + row.id}>
          Editar
        </Link>
    },
  ];

  // Visualização de Calendário
  const getDiasNoMes = (mes: number, ano: number) => {
    return new Date(ano, mes + 1, 0).getDate();
  };

  const getPrimeiroDiaSemana = (mes: number, ano: number) => {
    return new Date(ano, mes, 1).getDay();
  };

  // Filtrar gastos para o calendário (por ano e tipo de gasto)
  const gastosParaCalendario = useMemo(() => {
    return gastos.filter((gasto) => {
      if (!gasto.data) return false;
      const dataGasto = new Date(gasto.data);
      const anoGasto = dataGasto.getFullYear();

      // Filtrar por ano
      if (anoGasto !== ano) return false;

      // Filtrar por tipo de gasto se houver filtro
      if (tipoGastoFiltro !== undefined && gasto.tipoGasto?.id !== tipoGastoFiltro) {
        return false;
      }

      return true;
    });
  }, [gastos, ano, tipoGastoFiltro]);

  const getGastosNaData = (data: Date): ReadGastoDto[] => {
    return gastosParaCalendario.filter((gasto) => {
      if (!gasto.data) return false;
      const dataGasto = new Date(gasto.data);
      return (
        dataGasto.getFullYear() === data.getFullYear() &&
        dataGasto.getMonth() === data.getMonth() &&
        dataGasto.getDate() === data.getDate()
      );
    });
  };

  const hoje = useMemo(() => {
    const h = new Date();
    return new Date(h.getFullYear(), h.getMonth(), h.getDate()).getTime();
  }, []);

  const renderMes = (mes: number) => {
    const diasNoMes = getDiasNoMes(mes, ano);
    const primeiroDiaSemana = getPrimeiroDiaSemana(mes, ano);
    const dias: (number | null)[] = [];

    for (let i = 0; i < primeiroDiaSemana; i++) {
      dias.push(null);
    }

    for (let dia = 1; dia <= diasNoMes; dia++) {
      dias.push(dia);
    }

    return (
      <div key={mes} className="col-md-3 mb-4">
        <div className="card h-100">
          <div className="card-header text-center bg-light">
            <strong>{MESES[mes]} {ano}</strong>
          </div>
          <div className="card-body p-2">
            <div className="calendario-mes">
              <div className="calendario-dias-semana">
                {diasDaSemana.map((dia, index) => (
                  <div key={index} className="calendario-dia-semana">
                    {dia}
                  </div>
                ))}
              </div>
              <div className="calendario-dias">
                {dias.map((dia, index) => {
                  if (dia === null) {
                    return <div key={index} className="calendario-dia vazio"></div>;
                  }

                  const data = new Date(ano, mes, dia);
                  const dataTime = new Date(
                    data.getFullYear(),
                    data.getMonth(),
                    data.getDate()
                  ).getTime();
                  const isHoje = dataTime === hoje;
                  const gastosNaData = getGastosNaData(data);
                  const temGasto = gastosNaData.length > 0;
                  const totalGastos = gastosNaData.reduce((sum, g) => {
                    const valor = Number(g.valor) || 0;
                    return sum + valor;
                  }, 0);

                  return (
                    <div
                      key={index}
                      className={`calendario-dia ${isHoje ? 'hoje' : ''} ${temGasto ? 'com-evento' : ''}`}
                      onClick={() => {
                        if (temGasto) {
                          abrirModalGasto(gastosNaData);
                        }
                      }}
                      style={{
                        cursor: temGasto ? 'pointer' : 'default'
                      }}
                      title={temGasto ? `${gastosNaData.length} gasto(s) - Total: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalGastos)}` : ''}
                    >
                      {dia}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleAnoChange = (value: string) => {
    const numValue = Number(value);
    if (numValue >= 2000 && numValue <= 9999) {
      setAno(numValue);
    }
  };

  const abrirProjecaoProximaCompra = () => {
    if (!tipoGastoFiltro) {
      toast.info('Selecione um tipo de gasto para ver a projeção.');
      return;
    }

    setModalProjecaoAberto(true);
    setProjecaoLoading(true);
    setProjecao(null);

    GetGastoProjecaoProximaCompra(tipoGastoFiltro)
      .then((resultado) => {
        setProjecao(resultado);
      })
      .catch((res) => {
        setModalProjecaoAberto(false);
        toast.error(res instanceof Error ? res.message : String(res));
      })
      .finally(() => {
        setProjecaoLoading(false);
      });
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <h3 className="pt-4">Gastos do Setor</h3>
          <span className="blockquote-footer pb-4 text-end">
            Controle de gastos do setor.
          </span>
        </div>
      </div>
      <hr />

      <div className="row mb-3">
        <div className="col-md-12">
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center gap-2">
              <label htmlFor="ano-input" className="mb-0">Ano:</label>
              <input
                id="ano-input"
                type="number"
                className="form-control"
                style={{ width: '100px' }}
                min="2000"
                max="9999"
                value={ano}
                onChange={(e) => handleAnoChange(e.target.value)}
              />
            </div>
            {visualizacao === 'calendario' && (
              <>
                <div className="d-flex align-items-center gap-2">
                  <label htmlFor="tipoGastoFiltroCalendario" className="mb-0">Tipo de Gasto:</label>
                  <select
                    id="tipoGastoFiltroCalendario"
                    className="form-select"
                    style={{ width: '200px' }}
                    value={tipoGastoFiltro || ''}
                    onChange={(e) => {
                      const value = e.target.value === '' ? undefined : Number(e.target.value);
                      setTipoGastoFiltro(value);
                    }}
                  >
                    <option value="">Todos</option>
                    {tipoGastos.map((tipo) => (
                      <option key={tipo.id} value={tipo.id}>
                        {tipo.descricao}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  disabled={!tipoGastoFiltro}
                  title={tipoGastoFiltro ? 'Projetar próxima compra deste tipo' : 'Selecione um tipo de gasto'}
                  onClick={abrirProjecaoProximaCompra}
                >
                  Projeção
                </button>
              </>
            )}
            <div className="btn-group ms-auto" role="group">
              <button
                type="button"
                className={`btn ${visualizacao === 'calendario' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setVisualizacao('calendario')}
              >
                Calendário
              </button>
              <button
                type="button"
                className={`btn ${visualizacao === 'lista' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setVisualizacao('lista')}
              >
                Lista
              </button>
            </div>
          </div>
        </div>
      </div>

      {visualizacao === 'calendario' ? (
        <div className="row">
          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(renderMes)}
        </div>
      ) : (
        gastosFiltradosPorAno && (
          <DataTable
            columns={colunas}
            data={gastosFiltradosPorAno}
            initialSortKey="data"
            onChange={handleBuscar}
            serverSide={false}
            states={tableState}
            totalRecords={gastosFiltradosPorAno.length}
            renderFilters={() => (
              <div className='row'>
                <div className="col-md-12">
                  <select id="tipoGastoFiltro" className="form-select" value={tipoGastoFiltro || ''}
                    onChange={(e) => {
                      const value = e.target.value === '' ? undefined : Number(e.target.value);
                      setTipoGastoFiltro(value);
                    }}
                  >
                    <option value="">Todos</option>
                    {tipoGastos.map((tipo) => (
                      <option key={tipo.id} value={tipo.id}>
                        {tipo.descricao}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          />
        )
      )}

      <ModalProjecaoProximaCompra
        show={modalProjecaoAberto}
        loading={projecaoLoading}
        projecao={projecao}
        onHide={() => setModalProjecaoAberto(false)}
      />

      <Modal show={modalGastoAberto} onHide={() => setModalGastoAberto(false)} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>
            Gastos do Dia - {gastosSelecionados.length > 0 && gastosSelecionados[0].data ? DataAmigavel(gastosSelecionados[0].data) : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {gastosSelecionados.length > 0 && (
            <>
              <div className="mb-3">
                <strong>Total de gastos:</strong> {gastosSelecionados.length}
              </div>
              <div className="mb-3">
                <strong>Valor total:</strong> {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                  gastosSelecionados.reduce((sum, g) => {
                    const valor = Number(g.valor) || 0;
                    return sum + valor;
                  }, 0)
                )}
              </div>
              <table className="table table-striped table-hover">
                <thead>
                  <tr>
                    <th>Quem Gastou</th>
                    <th>Tipo de Gasto</th>
                    <th>Valor</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {gastosSelecionados.map((gasto) => (
                    <tr key={gasto.id}>
                      <td>
                        {gasto.quemGastou ? `${gasto.quemGastou.nome} ${gasto.quemGastou.sobrenome || ''}`.trim() : ''}
                      </td>
                      <td>{gasto.tipoGasto?.descricao || ''}</td>
                      <td>
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(gasto.valor) || 0)}
                      </td>
                      <td>
                        <Link className="link-secondary" to={Links.Gasto_Editar + '/' + gasto.id}>
                          Editar
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setModalGastoAberto(false)}>
            Fechar
          </button>
        </Modal.Footer>
      </Modal>

      {visualizacao === 'calendario' && (
        <style>{`
          .calendario-mes {
            display: flex;
            flex-direction: column;
          }
          
          .calendario-dias-semana {
            display: grid;
            grid-template-columns: repeat(7, 1fr);
            gap: 2px;
            margin-bottom: 4px;
          }
          
          .calendario-dia-semana {
            text-align: center;
            font-weight: bold;
            font-size: 0.75rem;
            padding: 4px 2px;
            color: #666;
          }
          
          .calendario-dias {
            display: grid;
            grid-template-columns: repeat(7, 1fr);
            gap: 2px;
          }
          
          .calendario-dia {
            aspect-ratio: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 0.85rem;
            border: 1px solid #e0e0e0;
            border-radius: 3px;
            transition: all 0.2s;
            min-height: 30px;
          }
          
          .calendario-dia.vazio {
            border: none;
            background: transparent;
          }
          
          .calendario-dia:hover:not(.vazio) {
            background-color: #f0f0f0;
          }
          
          .calendario-dia.hoje {
            background-color: #ffff76;
            font-weight: bold;
          }
          
          .calendario-dia.com-evento {
            background-color: #28a745 !important;
            color: white !important;
            font-weight: bold;
          }
          
          .calendario-dia.com-evento:hover {
            background-color: #218838 !important;
          }
        `}</style>
      )}
    </>
  );
}